import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Link,
  Redirect,
  useHistory,
} from 'react-router-dom';
import { auth, db, signOut, updatePassword, updateProfile, reauthenticateWithCredential, EmailAuthProvider, updateDoc } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import Schedule from './components/Schedule';
import Profile from './components/Profile';
import Leaderboard from './components/Leaderboard';
import AdminStats from './components/AdminStats';
import MatchDetails from './components/MatchDetails';
import DivisionGenerator from './components/DivisionGenerator';
import Landing from './components/Landing';
import LandingPage from './components/LandingPage';
import GeneratorTimova from './components/GeneratorTimova';
import RasporedTurnira from './components/RasporedTurnira';
import SemaforOnline from './components/SemaforOnline';
import PenaltyShootout from './components/PenaltyShootout';
import KojiSiTipIgraca from './components/KojiSiTipIgraca';
import Nogomandala from './components/Nogomandala';
import CreateTeam from './components/CreateTeam';
import TeamSettings from './components/TeamSettings';
import TeamScope from './components/TeamScope';
import OwnerOnly from './components/OwnerOnly';
import Home from './components/Home';
import MyDashboard from './components/MyDashboard';
import NotFoundPage from './components/NotFoundPage';
import {
  AccountDeletion,
  PrivacyPolicy,
  TermsOfUse,
} from './components/LegalPage';
import { TeamProvider, useTeam } from './contexts/TeamContext';
import { LanguageProvider, useTranslation } from './contexts/LanguageContext';
import { useTeamPath } from './hooks/useTeamPath';
import { useIsTeamOwner, useCanEditTeam } from './hooks/useIsTeamOwner';
import { claimMembership, normalizeEmail } from './utils/membership';
import { APP_VERSION, APP_COMMIT } from './version';
import LanguageSwitcher from './components/LanguageSwitcher';
import ChangePasswordPrompt from './components/ChangePasswordPrompt';
import './App.css';

const PasswordInput = ({ value, onChange, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        className="form-input"
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder || '••••••••'}
        style={{ paddingRight: 40 }}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: '1rem', lineHeight: 1, padding: 2 }}
        tabIndex={-1}
      >
        {show ? '🙈' : '👁️'}
      </button>
    </div>
  );
};

const UserEditModal = ({ user, onClose }) => {
  const [displayName, setDisplayName] = useState(user.displayName || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      if (displayName !== user.displayName) {
        await updateProfile(auth.currentUser, { displayName });
        await updateDoc(doc(db, 'users', auth.currentUser.uid), { displayName });
      }
      if (newPassword) {
        if (!currentPassword) { setError('Unesi trenutnu lozinku za promjenu lozinke.'); setSaving(false); return; }
        if (newPassword !== confirmPassword) { setError('Nova lozinka i potvrda se ne podudaraju.'); setSaving(false); return; }
        if (newPassword.length < 6) { setError('Lozinka mora imati najmanje 6 znakova.'); setSaving(false); return; }
        const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
        await reauthenticateWithCredential(auth.currentUser, credential);
        await updatePassword(auth.currentUser, newPassword);
      }
      setSuccess('Promjene su spremljene.');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') setError('Trenutna lozinka nije ispravna.');
      else setError(err.message || 'Greška pri spremanju.');
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 6 };
  const labelStyle = { fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em' };
  const hintStyle = { fontSize: '0.73rem', color: 'var(--text-3)', marginTop: 2 };

  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'lb-modal-fade 0.2s ease-out',
    }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)' }} onClick={onClose} />
      <div style={{
        position: 'relative', zIndex: 1,
        background: 'var(--surface, #1a2235)',
        border: '1px solid var(--border, #2a3442)',
        borderRadius: 14,
        padding: '24px 28px 28px',
        width: '100%', maxWidth: 400,
        boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>Uredi profil</h3>
          <button onClick={onClose} className="lb-modal-close">×</button>
        </div>

        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Ime i prezime</label>
            <input className="form-input" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Ime Prezime" />
          </div>

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 18, display: 'flex', flexDirection: 'column', gap: 18 }}>
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-3)', fontWeight: 600 }}>Promjena lozinke <span style={{ fontWeight: 400 }}>(neobavezno)</span></p>

            <div style={fieldStyle}>
              <label style={labelStyle}>Trenutna lozinka</label>
              <PasswordInput value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Nova lozinka</label>
              <PasswordInput value={newPassword} onChange={e => setNewPassword(e.target.value)} />
              <span style={hintStyle}>Minimum 6 znakova</span>
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Potvrdi novu lozinku</label>
              <PasswordInput value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
            </div>
          </div>

          {error && <p style={{ color: 'var(--danger, #f87171)', fontSize: '0.85rem', margin: 0 }}>{error}</p>}
          {success && <p style={{ color: 'var(--accent, #00f5a0)', fontSize: '0.85rem', margin: 0 }}>{success}</p>}

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button type="submit" className="btn btn-primary btn-lg" style={{ flex: 1 }} disabled={saving}>
              {saving ? 'Sprema...' : 'Spremi promjene'}
            </button>
            <button type="button" className="btn btn-secondary btn-lg" onClick={onClose} disabled={saving}>
              Odustani
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

const NavBar = ({ user, menuOpen, toggleMenu, closeMenu, onLogout }) => {
  const tp = useTeamPath();
  const isTeamOwner = useIsTeamOwner(user);
  const canEditTeam = useCanEditTeam(user);
  const { teamId } = useTeam();
  const { t } = useTranslation();
  const hasScopedTeam = Boolean(teamId);
  const [showUserEdit, setShowUserEdit] = useState(false);

  return (
    <nav className="navbar">
      <h1>StatArena</h1>
      <div className="hamburger" onClick={toggleMenu}>☰</div>
      <div className={`navbar-links ${menuOpen ? 'open' : ''}`}>
        <Link to={hasScopedTeam ? tp('/') : '/'} onClick={closeMenu}>{t('nav.home')}</Link>
        {hasScopedTeam && <Link to={tp('/profile')} onClick={closeMenu}>{t('nav.profile')}</Link>}
        {hasScopedTeam && canEditTeam && (
          <Link to={tp('/schedule')} onClick={closeMenu}>{t('nav.management')}</Link>
        )}
        {hasScopedTeam && <Link to={tp('/division')} onClick={closeMenu}>{t('nav.division')}</Link>}
        {hasScopedTeam && isTeamOwner && (
          <Link to={tp('/settings')} onClick={closeMenu}>{t('nav.settings')}</Link>
        )}
        <Link to="/directory" onClick={closeMenu}>{t('nav.teams')}</Link>
        {user.isAdmin && <Link to="/admin-stats" onClick={closeMenu}>Admin</Link>}
        <LanguageSwitcher />
        {(user.displayName || user.email) && (
          <span
            onClick={() => setShowUserEdit(true)}
            style={{
              fontSize: '0.82rem',
              color: 'var(--text-2, #94a3b8)',
              padding: '4px 8px',
              borderRadius: 6,
              background: 'rgba(255,255,255,0.05)',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
            }}
            title="Uredi profil"
          >
            {user.displayName || user.email}
          </span>
        )}
        {showUserEdit && <UserEditModal user={user} onClose={() => setShowUserEdit(false)} />}
        <Link to="/pravila-privatnosti" onClick={closeMenu} style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>{t('nav.privacy')}</Link>
        <button onClick={() => { onLogout(); closeMenu(); }}>{t('nav.logout')}</button>
      </div>
    </nav>
  );
};

const AuthedApp = ({ user, menuOpen, toggleMenu, closeMenu, onLogout }) => (
  <>
    <NavBar
      user={user}
      menuOpen={menuOpen}
      toggleMenu={toggleMenu}
      closeMenu={closeMenu}
      onLogout={onLogout}
    />

    <div className="container">
      <Switch>
        <Route path="/directory" component={() => <Landing user={user} />} />
        <Route path="/create-team" component={() => <CreateTeam user={user} />} />

        <Route
          path="/t/:slug/match/:id"
          component={() => (
            <TeamScope><MatchDetails user={user} /></TeamScope>
          )}
        />
        <Route
          path="/t/:slug/profile/:email"
          component={() => (
            <TeamScope><Profile user={user} /></TeamScope>
          )}
        />
        <Route
          path="/t/:slug/profile"
          component={() => (
            <TeamScope><Profile user={user} /></TeamScope>
          )}
        />
        <Route
          path="/t/:slug/leaderboard"
          component={() => (
            <TeamScope><Leaderboard /></TeamScope>
          )}
        />
        <Route
          path="/t/:slug/division"
          component={() => (
            <TeamScope><DivisionGenerator /></TeamScope>
          )}
        />
        <Route
          path="/t/:slug/schedule"
          component={() => (
            <TeamScope>
              <OwnerOnly user={user}>
                <Schedule user={user} />
              </OwnerOnly>
            </TeamScope>
          )}
        />
        <Route
          path="/t/:slug/settings"
          component={() => (
            <TeamScope>
              <OwnerOnly user={user} strict>
                <TeamSettings />
              </OwnerOnly>
            </TeamScope>
          )}
        />
        <Route
          path="/t/:slug"
          exact
          component={() => (
            <TeamScope><Home /></TeamScope>
          )}
        />

        <Route path="/login" render={() => <Redirect to="/" />} />
        <Route path="/register" render={() => <Redirect to="/" />} />
        <Route path="/" exact component={() => <MyDashboard user={user} />} />
        {user.isAdmin && (
          <Route path="/admin-stats" component={() => <AdminStats user={user} />} />
        )}

        <Route component={() => <NotFoundPage loggedIn />} />
      </Switch>
    </div>
  </>
);

const PublicNav = () => {
  const { t } = useTranslation();

  return (
    <header className="public-nav">
      <div className="public-nav-inner">
        <Link to="/" className="public-nav-brand">
          <span className="public-nav-mark" role="img" aria-label={t('common.brandBall')}>⚽</span>
          <span className="public-nav-text">StatArena</span>
        </Link>
        <nav className="public-nav-links">
          <Link to="/directory">{t('nav.teams')}</Link>
          <Link to="/pravila-privatnosti">{t('nav.privacy')}</Link>
          <LanguageSwitcher />
          <Link to="/login" className="public-nav-login">{t('nav.login')}</Link>
          <Link to="/register" className="btn btn-primary public-nav-cta">
            {t('nav.register')}
          </Link>
        </nav>
      </div>
    </header>
  );
};

const PublicShell = ({ children }) => (
  <>
    <PublicNav />
    <div className="container">{children}</div>
  </>
);

const AnonApp = ({ setUser }) => {
  const { t } = useTranslation();

  return (
  <Switch>
    <Route
      path="/pravila-privatnosti"
      render={() => (
        <PublicShell>
          <PrivacyPolicy />
        </PublicShell>
      )}
    />
    <Route
      path="/uvjeti-koristenja"
      render={() => (
        <PublicShell>
          <TermsOfUse />
        </PublicShell>
      )}
    />
    <Route
      path="/brisanje-racuna"
      render={() => (
        <PublicShell>
          <AccountDeletion />
        </PublicShell>
      )}
    />
    <Route
      path="/login"
      render={() => (
        <div className="auth-container">
          <Login />
          <p>
            {t('auth.noAccount')} <Link to="/register">{t('auth.registerHere')}</Link>.
          </p>
        </div>
      )}
    />
    <Route
      path="/register"
      render={() => (
        <div className="auth-container">
          <Register setUser={setUser} />
          <p>
            {t('auth.alreadyAccount')} <Link to="/login">{t('auth.loginHere')}</Link>.
          </p>
        </div>
      )}
    />
    <Route
      path="/forgot-password"
      render={() => (
        <div className="auth-container">
          <ForgotPassword />
        </div>
      )}
    />

    {/* Javni pregled timova - bez prijave */}
    <Route
      path="/directory"
      render={() => (
        <PublicShell>
          <Landing />
        </PublicShell>
      )}
    />
    <Route
      path="/t/:slug/match/:id"
      render={() => (
        <PublicShell>
          <TeamScope>
            <MatchDetails user={null} />
          </TeamScope>
        </PublicShell>
      )}
    />
    <Route
      path="/t/:slug/profile/:email"
      render={() => (
        <PublicShell>
          <TeamScope>
            <Profile user={null} />
          </TeamScope>
        </PublicShell>
      )}
    />
    <Route
      path="/t/:slug/profile"
      render={() => (
        <PublicShell>
          <TeamScope>
            <Profile user={null} />
          </TeamScope>
        </PublicShell>
      )}
    />
    <Route
      path="/t/:slug/leaderboard"
      render={() => (
        <PublicShell>
          <TeamScope>
            <Leaderboard />
          </TeamScope>
        </PublicShell>
      )}
    />
    <Route
      path="/t/:slug/division"
      render={() => (
        <PublicShell>
          <TeamScope>
            <DivisionGenerator />
          </TeamScope>
        </PublicShell>
      )}
    />
    <Route
      path="/t/:slug"
      exact
      render={() => (
        <PublicShell>
          <TeamScope>
            <Home />
          </TeamScope>
        </PublicShell>
      )}
    />

    <Route path="/" exact component={LandingPage} />
    <Route
      render={() => (
        <PublicShell>
          <NotFoundPage />
        </PublicShell>
      )}
    />
  </Switch>
  );
};

const AppBody = () => {
  const [user, setUser] = useState(null);
  const history = useHistory();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => setMenuOpen(!menuOpen);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (!authUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        let isAdmin = false;
        let displayName = authUser.displayName || '';

        const byUidSnap = await getDoc(doc(db, 'users', authUser.uid));
        if (byUidSnap.exists()) {
          isAdmin = byUidSnap.data().isAdmin === true;
          displayName = byUidSnap.data().displayName || displayName;
        } else if (authUser.email) {
          const byEmailSnap = await getDoc(
            doc(db, 'users', normalizeEmail(authUser.email))
          );
          if (byEmailSnap.exists()) {
            isAdmin = byEmailSnap.data().isAdmin === true;
            displayName = byEmailSnap.data().displayName || displayName;
          }
        }

        if (authUser.email) {
          const profileData = {
            uid: authUser.uid,
            email: normalizeEmail(authUser.email),
            updatedAt: serverTimestamp(),
          };
          if (displayName) profileData.displayName = displayName;
          await setDoc(doc(db, 'users', authUser.uid), profileData, { merge: true });
        }

        const needsPasswordChange = byUidSnap.exists()
          ? byUidSnap.data().needsPasswordChange === true
          : false;

        setUser({
          uid: authUser.uid,
          email: authUser.email,
          displayName,
          isAdmin,
          needsPasswordChange,
        });

        if (authUser.email) {
          claimMembership(authUser.uid, normalizeEmail(authUser.email), displayName).catch((err) => {
            console.warn('Claim membership pri loginu nije uspio (nastavljamo):', err);
          });
        }
      } catch (error) {
        console.error('Greška pri dohvaćanju korisnika iz Firestore:', error);
        // Firestore failed but auth is valid — set minimal user so they stay logged in
        setUser({
          uid: authUser.uid,
          email: authUser.email,
          displayName: authUser.displayName || '',
          isAdmin: false,
          needsPasswordChange: false,
        });
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      Object.keys(localStorage)
        .filter(k => k.startsWith('team-unlocked:'))
        .forEach(k => localStorage.removeItem(k));
      setUser(null);
      history.push('/login');
    } catch (error) {
      console.error('Greška pri odjavi:', error);
    }
  };

  if (loading) {
    return <div className="loading-screen">{t('common.loading')}</div>;
  }

  return (
    <div>
      <Switch>
        {/* Javne rute dostupne svima (i logged-in i anon) - bez NavBara */}
        <Route path="/generator-timova" component={GeneratorTimova} />
        <Route path="/raspored-turnira" component={RasporedTurnira} />
        <Route path="/semafor-online" component={SemaforOnline} />
        <Route path="/penalty-shootout" component={PenaltyShootout} />
        <Route path="/koji-si-igrac" component={KojiSiTipIgraca} />
        <Route path="/nogomandala" component={Nogomandala} />
        <Route path="/pravila-privatnosti" component={PrivacyPolicy} />
        <Route path="/uvjeti-koristenja" component={TermsOfUse} />
        <Route path="/brisanje-racuna" component={AccountDeletion} />
        <Route>
          {user ? (
            user.needsPasswordChange ? (
              <ChangePasswordPrompt
                user={user}
                onDone={() => setUser({ ...user, needsPasswordChange: false })}
              />
            ) : (
              <AuthedApp
                user={user}
                menuOpen={menuOpen}
                toggleMenu={toggleMenu}
                closeMenu={closeMenu}
                onLogout={handleLogout}
              />
            )
          ) : (
            <AnonApp setUser={setUser} />
          )}
        </Route>
      </Switch>

      <div className="version-badge" title={`Build: ${APP_COMMIT}`}>
        v{APP_VERSION} · {APP_COMMIT}
      </div>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <LanguageProvider>
        <TeamProvider>
          <AppBody />
        </TeamProvider>
      </LanguageProvider>
    </Router>
  );
};

export default App;
